import React, { useState, useEffect } from 'react';
import DeleteIcon from '@material-ui/icons/Delete';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { Link } from '@hid-galaxy-ui/galaxy-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Api from '../../api';
import ENDPOINTS, { ENDPOINTS_SEGMENTS } from '../../constants/endpoints';
import { getAuth0AccessToken } from '../../utils/authService';
import {
  tenantIdSelector,
  tokenSelector,
} from '../../store/selectors/formSessionSelectors';

const useStyles = () =>
  makeStyles((theme) => ({
    filePreviewContainer: {
      display: 'flex',
      width: '100%',
      marginBottom: `${theme.custom.margin.m20} ${theme.custom.margin.m20 / 2}`,
    },
    filePreview: {
      position: 'relative',
      width: '218px',
      height: '218px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      transition: 'all 300ms ease',
      '&:hover': {
        boxShadow: '0px 3px 3px rgba(0, 0, 0, 0.3)',
        cursor: 'pointer',
      },
    },
    filePreviewButtonContainer: {
      position: 'absolute',
      width: '40px',
      height: '40px',
      backgroundColor: '#d8d8d8',
      borderRadius: '50%',
      cursor: 'pointer',
      right: '-20px',
      top: 0,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      border: 'none',
    },
    filePreviewButton: {
      fontSize: 25,
      color: '#000',
    },
    filePreviewImage: {
      maxWidth: '100%',
      maxHeight: '218px',
    },
  }));

const FilePreview = ({
  previewMode,
  file,
  localBlobUrl,
  deleteFile,
  useAppHubApi,
}) => {
  const classes = useStyles()();
  const { t } = useTranslation();
  const tenantId = useSelector(tenantIdSelector);
  const formSessionToken = useSelector(tokenSelector);
  const [secureFileUrl, setSecureFileUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // If localBlobUrl is provided, use it; otherwise fetch from backend
  const shouldFetchFromBackend =
    !localBlobUrl &&
    file &&
    (file.startsWith('http://') || file.startsWith('https://'));

  const isPdf = file && file.toLowerCase().endsWith('.pdf');

  useEffect(() => {
    let objectUrl = null;

    const getSecurePhoto = async (fileUrl) => {
      try {
        setIsLoading(true);
        // Use App Hub API only for logged-in account documents from enduser-accounts
        const url = useAppHubApi
          ? ENDPOINTS.DOWNLOAD_SECURE_FILE
          : ENDPOINTS.FORM_SESSION_IMAGE_PREVIEW.replace(
              ENDPOINTS_SEGMENTS.TENANT_ID,
              tenantId,
            ).replace(ENDPOINTS_SEGMENTS.FORM_SESSION_TOKEN, formSessionToken);
        const token = getAuth0AccessToken();

        const response = await Api.service.request({
          method: 'POST',
          url,
          data: { fileUrl },
          responseType: 'blob',
          headers: token ? { Authorization: token } : {},
        });

        if (response && response instanceof Blob) {
          // Determine the correct MIME type based on file extension
          let mimeType = response.type;

          if (!mimeType || mimeType === 'application/octet-stream') {
            if (fileUrl.toLowerCase().endsWith('.pdf')) {
              mimeType = 'application/pdf';
            } else if (fileUrl.toLowerCase().match(/\.(jpg|jpeg)$/i)) {
              mimeType = 'image/jpeg';
            } else if (fileUrl.toLowerCase().endsWith('.png')) {
              mimeType = 'image/png';
            } else if (fileUrl.toLowerCase().endsWith('.gif')) {
              mimeType = 'image/gif';
            } else if (fileUrl.toLowerCase().endsWith('.webp')) {
              mimeType = 'image/webp';
            }
          }

          const blob =
            mimeType !== response.type
              ? new Blob([response], { type: mimeType })
              : response;

          objectUrl = URL.createObjectURL(blob);
          setSecureFileUrl(objectUrl);
        } else {
          setSecureFileUrl('');
        }
      } catch (error) {
        setSecureFileUrl('');
      } finally {
        setIsLoading(false);
      }
    };

    // Use local blob URL if provided, otherwise fetch from backend
    if (localBlobUrl) {
      setSecureFileUrl(localBlobUrl);
      setIsLoading(false);
    } else if (shouldFetchFromBackend) {
      // Fetch from backend for remote URLs
      getSecurePhoto(file);
    } else {
      setSecureFileUrl(file);
      setIsLoading(false);
    }

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [
    file,
    shouldFetchFromBackend,
    tenantId,
    formSessionToken,
    localBlobUrl,
    useAppHubApi,
  ]);

  const displayUrl = secureFileUrl;
  return (
    <>
      <div className={classes.filePreviewContainer}>
        <div className={classes.filePreview}>
          {!previewMode && (
            <button
              type="button"
              className={classes.filePreviewButtonContainer}
              onClick={deleteFile}
            >
              <DeleteIcon className={classes.filePreviewButton} />
            </button>
          )}

          {isPdf ? (
            <>
              {isLoading ? (
                <div>Loading...</div>
              ) : (
                <iframe
                  src={displayUrl ? `${displayUrl}#toolbar=0` : ''}
                  width="218"
                  height="218"
                  title="PDF Preview"
                  style={{ border: 'none' }}
                />
              )}
            </>
          ) : (
            <>
              {isLoading ? (
                <div>Loading...</div>
              ) : (
                <img
                  className={classes.filePreviewImage}
                  src={displayUrl}
                  alt=""
                  onError={(e) => {
                    console.error('Image failed to load:', displayUrl);
                    console.error('Error event:', e);
                  }}
                />
              )}
            </>
          )}
        </div>
      </div>
      {isPdf && secureFileUrl && (
        <div className="hid-layout__mt-02">
          <Link link={displayUrl} target="_blank" rel="noopener noreferrer">
            {t('VIEW_DOCUMENT')}
          </Link>
        </div>
      )}
    </>
  );
};

FilePreview.propsTypes = {
  previewMode: PropTypes.bool,
  file: PropTypes.string.isRequired,
  localBlobUrl: PropTypes.string,
  deleteFile: PropTypes.func,
  useAppHubApi: PropTypes.bool,
};

FilePreview.defaultProps = {
  previewMode: false,
  localBlobUrl: null,
  deleteFile: () => {},
  useAppHubApi: false,
};

export default FilePreview;
